/**
 * Revise report page for LDT orders.
 * LDT CS/Sales users can revise an order's report from this page.
 */
import { useLoaderData } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { LdtMetastaticProstateReviseReportForm } from '@ldt/features/orders/metastatic_prostate_revise_report_form';
import { LdtProstateBiopsyReviseReportForm } from '@ldt/features/orders/prostate_biopsy_revise_report_form';
import { LdtProstatePostRPReviseReportForm } from '@ldt/features/orders/prostate_post_rp_revise_report_form';
import { OrderRepository } from 'src/api/order_repository';
import { orderKeys } from 'src/constants/orders';
import { LdtOrderForReviseReportFieldsFragment, SpecimenType } from 'src/types';

export const OrderReviseReportPage = ({
  orderRepository,
  // In background mode (e.g. user opened CREATE ORDER from appbar), loader does not run;
  // orderId is passed as a prop and we build query keys to match the loader format.
  backgroundOrderId,
}: {
  orderRepository: OrderRepository;
  backgroundOrderId?: string;
}) => {
  const loaderData = useLoaderData() as { orderId: string } | null;

  const orderId = (loaderData?.orderId ?? backgroundOrderId)!;

  // In background mode, loader data is from the active route (e.g. order form), not this page.
  // Use the same key shape as orderReportRevisionLoader so cache is consistent.
  const orderForReviseReportQueryKey = orderKeys.forReviseReport(orderId);
  const { data: order } =
    useSuspenseQuery<LdtOrderForReviseReportFieldsFragment | null>({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: orderForReviseReportQueryKey,
      queryFn: () => {
        return (orderRepository as LdtOrderRepository).getOrderForReviseReport(
          orderId,
        );
      },
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  // Loader throws an error if the order is not found. This is just a sanity check.
  if (!order) {
    return <>Order not found</>;
  }

  const ldtOrderRepository = orderRepository as LdtOrderRepository;

  if (order.specimenType === SpecimenType.ProstateBiopsy) {
    return (
      <LdtProstateBiopsyReviseReportForm
        key={order.id}
        order={order}
        ldtOrderRepository={ldtOrderRepository}
      />
    );
  } else if (order.specimenType === SpecimenType.ProstatePostRp) {
    return (
      <LdtProstatePostRPReviseReportForm
        key={order.id}
        order={order}
        ldtOrderRepository={ldtOrderRepository}
      />
    );
  } else if (order.specimenType === SpecimenType.MetastaticProstateBiopsy) {
    return (
      <LdtMetastaticProstateReviseReportForm
        key={order.id}
        order={order}
        ldtOrderRepository={ldtOrderRepository}
      />
    );
  } else {
    return <>Cannot revise reports for specimen type: {order.specimenType}</>;
  }
};
